import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

interface CertificateBadgeProps {
  primaryColor?: string;
  isWhitelabel?: boolean;
  orgLogo?: string | null;
  size?: number;
}

const DEFAULT_PRIMARY_COLOR = '#2563EB';

function CertificateBadgeComponent({
  primaryColor = DEFAULT_PRIMARY_COLOR,
  isWhitelabel = false,
  orgLogo,
  size = 154,
}: CertificateBadgeProps) {
  const { t } = useTranslation();
  const color = primaryColor || DEFAULT_PRIMARY_COLOR;
  const oneTeamLogo = t('common:one_team_logo', 'oneteam');
  // Certificate text for the circular border - one continuous line
  const certificateText = !isWhitelabel ? oneTeamLogo + ' '
  + t('learning:certificate_of_completion', 'Certificate of completion')
  : t('learning:certificate_of_completion', 'Certificate of completion');

  // Calculate scale for different sizes
  const scale = size / 154;
  const height = Math.round(189 * scale);

  // Center point of the badge
  const cx = 76.4261;
  const cy = 76.4271;
  // Radius for the text path (between inner circle and outer edge)
  const textRadius = 55;
  const paddedRadius = textRadius - 1;
  const circleTextPath = `
  M ${cx - paddedRadius},${cy} 
  A ${paddedRadius},${paddedRadius} 0 1,1 ${cx + paddedRadius},${cy} 
  A ${paddedRadius},${paddedRadius} 0 1,1 ${cx - paddedRadius},${cy}
`;

  // Calculate adaptive font size based on text length
  const baseTextLength = 34;
  const baseFontSize = 9;
  const textLength = certificateText.replace(/\s/g, '').length; // Count non-space characters

  // Scale down font for longer text to fit the circle
  const scaleFactor = Math.min(1, baseTextLength / textLength);
  const adaptiveFontSize = Math.max(6, baseFontSize * scaleFactor); // Min font size of 6

  // Letter spacing: shorter text needs MORE spacing to spread around circle
  // Calibrated: ~30 chars (with oneteam) → 2.5 spacing, ~23 chars (without) → 5 spacing
  const adaptiveLetterSpacing = Math.max(1.5, Math.min(6, 13.3 - 0.36 * textLength));

  // Calculate if there's a gap (short text doesn't fill circle)
  // If text is short, rotate to put gap at bottom; if text is long, start from top-left
  // Rotation of -50° aligns with PDF export's startAngle of 130° (180° - 50° = 130°)
  const hasGap = textLength < 40; // Approximate threshold for when text fills the circle
  const rotationAngle = hasGap ? -50 : 45; // Gap at bottom vs gap at left (if any)
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 154 189"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Ribbons - use primary color */}
      <path
        d="M37.3519 133.202H66.3519V188.202L51.8519 173.202L37.3519 188.202V133.202Z"
        fill={color}
      />
      <path
        d="M86.3519 133.202H115.352V188.202L100.852 173.202L86.3519 188.202V133.202Z"
        fill={color}
      />

      {/* Badge border - black */}
      <path
        d={
          'M70.3334 4.47789C73.9368 1.17444 79.4673 1.17438 83.0707 4.47789C84.7523 6.01963 87.2191 6.34419 '
          + '89.2425 5.29039C93.5782 3.03199 98.9216 4.46359 101.547 8.58727C102.773 10.5116 105.071 11.4635 '
          + '107.298 10.9691C112.071 9.90982 116.861 12.6757 118.329 17.3382C119.015 19.5144 120.989 21.0293 '
          + '123.269 21.1283C128.153 21.3405 132.064 25.2513 132.276 30.1351C132.375 32.4145 133.89 34.3881 '
          + '136.066 35.0736C140.728 36.5425 143.493 41.3334 142.434 46.1058C141.94 48.333 142.892 50.6315 '
          + '144.817 51.8568C148.94 54.4823 150.372 59.8249 148.114 64.1605C147.06 66.184 147.384 68.6516 '
          + '148.926 70.3334C152.229 73.9367 152.23 79.4673 148.926 83.0707C147.384 84.7523 147.06 87.2191 '
          + '148.114 89.2425C150.372 93.5782 148.94 98.9216 144.817 101.547C142.892 102.773 141.94 105.071 '
          + '142.434 107.298C143.493 112.071 140.728 116.86 136.066 118.329C133.89 119.015 132.375 120.99 '
          + '132.276 123.269C132.064 128.153 128.153 132.064 123.269 132.276C120.99 132.375 119.015 133.89 '
          + '118.329 136.066C116.86 140.728 112.071 143.493 107.298 142.434C105.071 141.94 102.773 142.892 '
          + '101.547 144.817C98.9216 148.94 93.5782 150.372 89.2425 148.114C87.2191 147.06 84.7523 147.384 '
          + '83.0707 148.926C79.4673 152.23 73.9367 152.229 70.3334 148.926C68.6516 147.384 66.184 147.06 '
          + '64.1605 148.114C59.8249 150.372 54.4823 148.94 51.8568 144.817C50.6315 142.892 48.333 141.94 '
          + '46.1058 142.434C41.3334 143.493 36.5425 140.728 35.0736 136.066C34.3881 133.89 32.4145 132.375 '
          + '30.1351 132.276C25.2513 132.064 21.3405 128.153 21.1283 123.269C21.0293 120.989 19.5144 119.015 '
          + '17.3382 118.329C12.6757 116.861 9.90982 112.071 10.9691 107.298C11.4635 105.071 10.5116 102.773 '
          + '8.58727 101.547C4.46359 98.9216 3.03199 93.5782 5.29039 89.2425C6.34419 87.2191 6.01963 84.7523 '
          + '4.47789 83.0707C1.17438 79.4673 1.17444 73.9368 4.47789 70.3334C6.01972 68.6516 6.34441 66.184 '
          + '5.29039 64.1605C3.03218 59.8249 4.46368 54.4823 8.58727 51.8568C10.5117 50.6314 11.4634 48.333 '
          + '10.9691 46.1058C9.90981 41.3334 12.6755 36.5425 17.3382 35.0736C19.5144 34.3881 21.0293 32.4145 '
          + '21.1283 30.1351C21.3404 25.2512 25.2512 21.3404 30.1351 21.1283C32.4145 21.0293 34.3881 19.5144 '
          + '35.0736 17.3382C36.5425 12.6755 41.3334 9.90981 46.1058 10.9691C48.333 11.4634 50.6314 10.5117 '
          + '51.8568 8.58727C54.4823 4.46368 59.8249 3.03218 64.1605 5.29039C66.184 6.34441 68.6516 6.01972 '
          + '70.3334 4.47789Z'
        }
        fill="#030712"
        stroke="white"
        strokeWidth="4"
      />

      <g clipPath="url(#clip0_badge)">
        {/* Inner white circle background */}
        <rect
          x="7.95153"
          y="7.95201"
          width="136.95"
          height="136.95"
          rx="68.475"
          fill="white"
        />

        {/* Circular text path - defined in defs */}
        <defs>
          {/* Full circle path starting from top, going clockwise */}
          <path
            id="circleTextPath"
            d={circleTextPath}
            fill="none"
          />


          <clipPath id="circleClip">
            <circle cx={cx} cy={cy} r="26" />
          </clipPath>
        </defs>

        {/* Circular text - one continuous line with adaptive sizing */}
        {/* Rotate to position: 135° puts gap at bottom for short text, 45° for long text */}
        <g transform={`rotate(${rotationAngle} ${cx} ${cy})`}>
          <text
            dy="2"
            dominantBaseline="central"
            fill="#71717A"
            fontSize={adaptiveFontSize}
            fontWeight="600"
            letterSpacing={adaptiveLetterSpacing}
            fontFamily="system-ui, -apple-system, sans-serif"
          >
            <textPath
              href="#circleTextPath"
              startOffset="0%"
              textAnchor="start"
            >
              {certificateText.toUpperCase()}
            </textPath>
          </text>
        </g>

        <g clipPath="url(#clip1_badge)">
          {/* Main colored circle - use primary color */}
          <circle
            cx={cx}
            cy={cy}
            r="40.8375"
            fill={color}
            stroke="#030712"
            strokeWidth="2.475"
          />

          {/* Logo in center */}
          {isWhitelabel && orgLogo ? (
            // Show org logo for whitelabel
            <image
              href={orgLogo}
              x={cx - 26}
              y={cy - 26}
              width="52"
              height="52"
              preserveAspectRatio="xMidYMid slice"
              clipPath="url(#circleClip)"
            />
          ) : (
            // Show Oneteam logo (white path)
            <path
              d={
                'M101.364 90.9919C103.867 86.7161 105.301 81.7391 105.301 76.4271C105.301 60.4799 92.3734 47.5521 '
                + '76.4261 47.5521C60.4789 47.5521 47.5511 60.4799 47.5511 76.4271C47.5511 81.7182 48.9743 86.6769 '
                + '51.4586 90.9413C59.7999 82.287 63.1396 73.2521 66.6645 63.5676C68.6098 58.2227 74.5197 55.4669 '
                + '79.8645 57.4123C85.2093 59.3576 87.9651 65.2675 86.0198 70.6123L85.9403 70.8306C82.3787 80.6171 '
                + '78.0857 92.4131 67.5319 103.906C70.3336 104.813 73.3227 105.302 76.4261 105.302C79.5057 105.302 '
                + '82.4727 104.82 85.2557 103.927C83.5792 102.103 82.0605 100.271 80.6775 98.4402C80.1966 97.8036 '
                + '80.1787 96.9353 80.6032 96.2599C84.8419 89.5171 87.5794 82.9707 89.7781 77.1231C90.0808 76.318 '
                + '91.2179 76.2716 91.6013 77.0417C93.9765 81.8124 96.9885 86.4586 101.364 90.9919Z'
              }
              fill="white"
            />
          )}
        </g>
      </g>

      {/* Outer border */}
      <rect
        x="9.60153"
        y="9.60201"
        width="133.65"
        height="133.65"
        rx="66.825"
        stroke="#030712"
        strokeWidth="3.3"
        fill="none"
      />

      <defs>
        <clipPath id="clip0_badge">
          <rect
            x="7.95153"
            y="7.95201"
            width="136.95"
            height="136.95"
            rx="68.475"
            fill="white"
          />
        </clipPath>
        <clipPath id="clip1_badge">
          <rect
            x="34.3515"
            y="34.352"
            width="84.15"
            height="84.15"
            rx="42.075"
            fill="white"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

export const CertificateBadge = memo(CertificateBadgeComponent);
